import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {BruteForceGameData, BruteForceInitData, IWinLineData} from "./BruteForceData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {BruteForce} from "../BruteForce";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {BruteForceWinConfigs} from "../BruteForceWinConfigs";
import {BruteForceBetlineConfig} from "../BruteForceBetlineConfig";
import {BruteForceGameConfig} from "../BruteForceGameConfig";
import {BruteForceGameMode} from "./BruteForceGameMode";
import {BetFeature} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetFeatureController";


export class BruteForceModel extends GameModel<BruteForceInitData, BruteForceGameData> {
    public buyFeatureName: string = "";
    public isRestore: boolean = false;
    public freespinsLeft: number;
    public mode: string;
    public nextMode: string;
    public betLineWins: IWinLineData[];
    public playedBetValue: number;
    public accumulatedRoundWin: number;
    public winLinesIndexArray: number[];
    public allWinPositions: boolean[][];
    public currentPaylines: number[][];
    public reels: string[][];
    public evaluatedArea: string[][];
    public totalSpinWinnings: number;
    public currentWinRatio: number;
    public parsedClientReels: string[][];
    public xNudgeLevels: number[];
    public xNudgeMultipliers: number[];
    public xNudgeSticky: boolean[];
    public toNudge: boolean[] = [false, false, false, false, false];
    public scatterLandedPos: number[];
    public totalScatterLanded: number;
    public scatterAnticipationReels: boolean[];
    public scatterAnticipationCopy: boolean[];
    public brokeTheBank: boolean;
    public isXNPartOfWin: boolean[];
    public boostedBet: boolean;

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);
        api.events.once("featureName", (data: string) => {
            this.buyFeatureName = data;
        });
        BruteForce.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, (betData: APIFeatureBetEventData) => {
            for (let index = 0; index < BruteForce.model.initData.featureBuyTimesBetValue.length; index++) {
                if (BruteForce.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    break;
                }
            }
        });

        BruteForce.apiPlugin.events.on(APIEvent.BET, (betData: APIFeatureBetEventData) => {
            for (let index = 0; index < BruteForce.model.initData.featureBuyTimesBetValue.length; index++) {
                if (BruteForce.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    break;
                }
            }
        });
    }

    protected onInit(data: BruteForceInitData): void {
        super.onInit(data);
        this.isRestore = data.isRestoreState;
        this.freespinsLeft = data.freespinsLeft;
        this.mode = data.mode;
        this.nextMode = data.nextMode;
        this.betLineWins = data.betLineWins;
        this.playedBetValue = data.playedBetValue;
        this.accumulatedRoundWin = data.accumulatedRoundWin;
        this.reels = data.reels;
        this.parsedClientReels = [];
        this.xNudgeLevels = data.xNudgeLevels;
        this.xNudgeMultipliers = data.xNudgeMultipliers;
        this.xNudgeSticky = data.xNudgeSticky;
        this.toNudge = [false, false, false, false, false];
        this.scatterAnticipationReels = [false, false, false, false, false];
        this.scatterAnticipationCopy = [false, false, false, false, false];
        this.brokeTheBank = false;
        this.isXNPartOfWin = [false, false, false, false, false];
        this.boostedBet = data.boostedBet;
    }

    protected onGame(data: BruteForceGameData): void {
        super.onGame(data);
        this.isRestore = false;
        this.freespinsLeft = data.freespinsLeft;
        this.mode = data.mode;
        this.nextMode = data.nextMode;
        this.betLineWins = data.betLineWins;
        this.playedBetValue = data.playedBetValue;
        this.accumulatedRoundWin = data.accumulatedRoundWin;
        this.totalSpinWinnings = data.totalSpinWinnings;
        this.currentWinRatio = this.winRatio();
        this.reels = data.reels; //buffer symbols are included in this
        this.evaluatedArea = data.evaluatedArea;
        this.toNudge = [false, false, false, false, false];
        this.currentPaylines = data.betLineWins.length > 0 ? this.createPayLinesList() : [];
        this.winLinesIndexArray = data.betLineWins.length > 0 ? this.checkBetLineNumber() : [];
        this.parsedClientReels = this.calculateClientReels(data.reels);
        this.allWinPositions = data.betLineWins.length > 0 ? this.parseWinPositions() : [];
        this.xNudgeLevels = data.xNudgeLevels;
        this.xNudgeMultipliers = data.xNudgeMultipliers;
        this.xNudgeSticky = data.xNudgeSticky;
        this.scatterLandedPos = this.parseScatterLanded(data.reels);
        this.totalScatterLanded = this.parseTotalScatters(this.scatterLandedPos);
        this.scatterAnticipationReels = this.parseScatterAnticipationReels();
        this.scatterAnticipationCopy = JSON.parse(JSON.stringify(this.scatterAnticipationReels));
        this.brokeTheBank = data.brokeTheBank;
        this.isXNPartOfWin = this.checkXNPartOfWin();
        this.boostedBet = data.boostedBet;
    }


    private winRatio(): number {
        if (this.betLineWins.length === 0) {
            return 0;
        }
        const betWinsData: IWinLineData[] = this.betLineWins;
        const clientTotalSpinWinnings = betWinsData.map((win) => win.multipliedAmount).reduce((a, b) => a + b, 0);
        return clientTotalSpinWinnings / this.playedBetValue;
    }

    get isBigWin(): boolean {
        if(BruteForce.apiPlugin.gameClientConfiguration.belowStakeWinRestriction) {
            if (this.boostedBet && this.mode === BruteForceGameMode.NORMAL) {
                if (this.buyFeatureName === "GUARANTEED_X_NUDGE" || this.buyFeatureName === "GUARANTEED_X_NUDGE_2" || this.buyFeatureName === "GUARANTEED_X_NUDGE_4") {
                    const activeBetFeature = BruteForce.apiPlugin.betFeatureController.getActiveBetFeature();
                    if (activeBetFeature) {
                        let boosterInfo: BetFeature | undefined = BruteForce.apiPlugin.betFeatureController.getFeatureData(activeBetFeature.name);
                        let currentPlayedValue: number = boosterInfo?.price! * this.playedBetValue;
                        return this.totalSpinWinnings > currentPlayedValue;
                    }
                }
            }
        }
        let winRatio: number = MathHelper.ceilToDecimals((this.totalSpinWinnings / this.playedBetValue), 2);
        return (winRatio >= BruteForceWinConfigs.WIN_RATIOS.bigWin[0].ratio);
    }

    private checkBetLineNumber(): number[] {
        let indexArray: number[] = [];
        for (let i: number = 0; i < this.currentPaylines.length; i++) {
            BruteForceBetlineConfig.betlines.forEach((value: number[], num: number) => {
                if (value.every((element: number, index) => element === this.currentPaylines[i][index])) {
                    indexArray.push(num);
                }
            });
        }
        // debugger;
        return indexArray;
    }

    private createPayLinesList(): number[][] {
        let serverPaylines: number[][] = [];
        for (let i: number = 0; i < BruteForce.model.gameData.betLineWins.length; i++) {
            serverPaylines.push(BruteForce.model.gameData.betLineWins[i].payline);
        }
        return serverPaylines;
    }

    private parseWinPositions(): boolean[][] {
        let winArray: boolean[][] = [];
        for (let x: number = 0; x < 5; x++) {
            winArray[x] = [];
            for (let y: number = 0; y < 5; y++) {
                winArray[x].push(false);
            }
        }
        this.betLineWins.forEach((item: IWinLineData, index: number) => {
            for (let i: number = 0; i < item.length; i++) {
                if (this.evaluatedArea[i][item.payline[i]] === item.symbol || this.evaluatedArea[i][item.payline[i]] === "XN") {
                    winArray[i][item.payline[i]] = true;
                }
            }
        });
        return winArray;
    }

    private calculateClientReels(currentReels: string[][]): string[][] {
        let myReels: string[][] = [];
        const allXNIndexes:number[][]=[];
        const snakeXNExists:boolean[]=[false,false,false,false,false];
        currentReels = this.removeBufferSym(JSON.parse(JSON.stringify(currentReels))); // remove buffer symbols
        currentReels.forEach((reel: string[], reelNo: number) => {
            let XNindexes: number[] = [];
            reel.forEach((symName: string, symNo: number) => {
                if(symName==="XN"){
                    XNindexes.push(symNo);
                }
            });
            if(XNindexes.length>1){
                let currentIndex:number=0;
                for(let check:number=1;check<XNindexes.length;check++){
                    if(XNindexes[check]===XNindexes[currentIndex]+1){
                        currentIndex=check;
                        continue;
                    }else{
                        snakeXNExists[reelNo] = true;
                        break;
                    }
                }
            }
            allXNIndexes.push(XNindexes);
        });
        snakeXNExists.forEach((value:boolean,reelIndex:number)=>{
            if(value){
                const alterReels:string[]=currentReels[reelIndex];
                const firstXNIndex:number=0;//always exist on 0;
                for(let i:number=1;i<allXNIndexes[reelIndex].length;i++){
                    alterReels.splice(allXNIndexes[reelIndex][i],1);
                    alterReels.unshift("XN");
                }
            }
        });
        //console.log(currentReels);
        //debugger;
        currentReels.forEach((reelArray: string[], reelID: number) => {
            myReels.push([]);
            let nudgeHeightCount: number = 0;
            let originalNudgePos: number = -2;
            let containsXNudge: boolean = false;
            reelArray.forEach((symName: string, symId: number, reelArray: string[]) => {
                if (symName === "XN") {
                    containsXNudge = true;
                    if (!BruteForce.allreelsView.reelModels[reelID].reelView.isReelContainingFullNudge) {
                        if (nudgeHeightCount === 0) {
                            originalNudgePos = symId;
                        } else {
                            symName = "B";
                        }
                        nudgeHeightCount++;
                    } else {
                        BruteForce.allreelsView.reelModels[reelID].reelView.nudgeHeight = 5;
                        originalNudgePos = JSON.parse(JSON.stringify(BruteForce.allreelsView.reelModels[reelID].reelView.nudgeReelPos));
                    }
                }
                if (nudgeHeightCount > 1 && originalNudgePos > 0 && containsXNudge) {
                    symName = "B";
                }
                myReels[reelID].push(symName);
            });
            BruteForce.allreelsView.reelModels[reelID].reelView.nudgeHeight = JSON.parse(JSON.stringify(nudgeHeightCount));
            BruteForce.allreelsView.reelModels[reelID].reelView.nudgeReelPos = JSON.parse(JSON.stringify(originalNudgePos));
            if (nudgeHeightCount > 0 && originalNudgePos > -2) {
                if (!((originalNudgePos === -1 && nudgeHeightCount === 1) || (originalNudgePos === 5 && nudgeHeightCount === 1))) {
                    if (!BruteForce.allreelsView.reelModels[reelID].reelView.isReelContainingFullNudge) {
                        this.toNudge[reelID] = true;
                        BruteForce.allreelsView.reelModels[reelID].stepsToNudge = 5 - nudgeHeightCount;
                    }
                }
            }
            /*   if (nudgeHeightCount === 5) {
                   BruteForce.allreelsView.reelModels[reelID].reelView.isReelContainingFullNudge = true;
               }*/

        });

        //adding back bufferSymbols
        myReels.forEach((symArray: string[], reelId: number) => {
            symArray.unshift(this.reels[reelId][0] === "XN" || this.toNudge[reelId] ? `L${Math.floor(Math.random() * 5) + 1}` : this.reels[reelId][0]);
            symArray.push(this.reels[reelId][6] === "XN" || this.toNudge[reelId] ? `L${Math.floor(Math.random() * 5) + 1}` : this.reels[reelId][6]);
            symArray.unshift(`L${Math.floor(Math.random() * 5) + 1}`);
            symArray.push(`L${Math.floor(Math.random() * 5) + 1}`);
            symArray.unshift(BruteForceGameConfig.GAME_SYMBOLS[`${Math.floor(Math.random() * 5) + 1}`]);
            symArray.push(BruteForceGameConfig.GAME_SYMBOLS[`${Math.floor(Math.random() * 5) + 1}`]);
        });

        return myReels;
    }

    private removeBufferSym(symArray: string[][]): string[][] {
        for (let reelId: number = 0; reelId < symArray.length; reelId++) {
            symArray[reelId] = symArray[reelId].slice(1, symArray[reelId].length - 1);
        }
        return symArray;
    }

    private parseScatterLanded(currentReels: string[][]): number[] {
        let scatterPosInReels: number[] = [-1, -1, -1, -1, -1];
        let reelData: string[][] = this.removeBufferSym(JSON.parse(JSON.stringify(currentReels)));
        reelData.forEach((reel: string[], reelNo: number) => {
            reel.forEach((symName: string, symNo) => {
                if (symName === "SC") {
                    scatterPosInReels[reelNo] = symNo;
                }
            });
        });
        return scatterPosInReels;
    }

    private parseScatterAnticipationReels(): boolean[] {
        let anticipationReel: boolean[] = [false, false, false, false, false];
        if (this.mode !== "NORMAL") {
            return anticipationReel;
        }
        if (BruteForce.apiPlugin.betFeatureController.getActiveBetFeature()?.name === "FREESPIN_3_SCATTERS" || BruteForce.apiPlugin.betFeatureController.getActiveBetFeature()?.name === "FREESPIN_4_SCATTERS") {
            return anticipationReel;
        }
        let counter: number = 0;
        if (this.totalScatterLanded >= 2) {
            this.scatterLandedPos.forEach((item: number, index: number) => {
                if (counter >= 2) {
                    anticipationReel[index] = true;
                    if (counter === 4 && index === 4) {
                        anticipationReel[index] = false;
                    }
                }
                if (item > -1) {
                    counter++;
                }
            });
        }
        // console.log("anticipation/nearwinreels", anticipationReel);
        return anticipationReel;
    }

    private parseTotalScatters(scatterPos: number[]): number {
        let totalScatters: number = 0;
        scatterPos.forEach((item: number) => {
            if (item > -1) {
                totalScatters++;
            }
        });
        return totalScatters;
    }

    private checkXNPartOfWin(): boolean[] {
        const isXNPartOfWin: boolean[] = [false, false, false, false, false];
        isXNPartOfWin.forEach((value: boolean, index: number) => {
            if (BruteForce.model.betLineWins.length > 0) {
                BruteForce.model.allWinPositions[index].forEach((value: boolean, symId: number) => {
                    if (value) {
                        if (BruteForce.model.evaluatedArea[index][symId] === "XN") {
                            isXNPartOfWin[index] = true;
                        }
                    }
                });
            }
        });

        return isXNPartOfWin;
    }
}